import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import profileStaff from "../../assets/svg/defultImage.svg";
import noDataFound from "../../assets/images/noDataFound.jpg";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import "./Clients.css";
import {
  Search,
  SlidersHorizontal,
  Download,
  Plus,
  Users,
  CheckCircle2,
  Crown,
  UserPlus,
  AlertTriangle,
  Phone,
  MessageCircle,
  MoreVertical,
  ChevronLeft,
  ChevronRight,
  Eye,
  Award,
  Medal,
  ChevronDown,
  X,
  Pencil,
  StickyNote,
  ClipboardList,
  CreditCard,
  Activity,
  Power,
  Trash2,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import NotFound from "../../components/SharedModule/NotFound";

const API_BASE_URL = "https://estbn.com/EstbnNew/public/api/portal/clients";
const OPTIONS_API_URL =
  "https://estbn.com/EstbnNew/public/api/portal/clients/options";

const IMAGE_BASE_URL = "https://estbn.com/EstbnNew/public";
const PER_PAGE = 15;

const TIER_THRESHOLDS = {
  gold: 50000,
  silver: 5000,
};

const getClientTier = (totalPoints = 0) => {
  if (totalPoints >= TIER_THRESHOLDS.gold) return "gold";
  if (totalPoints >= TIER_THRESHOLDS.silver) return "silver";
  return "bronze";
};

const TIER_LABEL = { gold: "Gold", silver: "Silver", bronze: "Bronze" };

const TIER_ICON = { gold: Crown, silver: Award, bronze: Medal };

const RISK_OPTIONS = [
  { value: "all", label: "Risk Level: All" },
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

const HAS_ORDER_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    return new Date(dateStr).toLocaleDateString("en-GB", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return null;
  }
};

const formatMoney = (value) => {
  const n = Number(value) || 0;
  return `${n.toLocaleString("en-US")} EGP`;
};

const seededValue = (seed, min, max) => {
  const x = Math.sin(seed * 999) * 10000;
  const frac = x - Math.floor(x);
  return Math.floor(frac * (max - min + 1)) + min;
};

const getRiskLevel = (score) => {
  if (score >= 60) return "high";
  if (score >= 30) return "medium";
  return "low";
};

const RISK_LABEL = { low: "Low", medium: "Medium", high: "High" };

const daysAgo = (dateStr) => {
  if (!dateStr) return Infinity;
  const diff = Date.now() - new Date(dateStr).getTime();
  return diff / (1000 * 60 * 60 * 24);
};

function CustomSelect({ value, onChange, options, className = "" }) {
  const [isOpen, setIsOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const handleOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  const selected = options.find((o) => o.value === value) || options[0];

  return (
    <div className={`cldb-cselect ${className}`} ref={wrapRef}>
      <button
        type="button"
        className={`cldb-cselect-trigger ${isOpen ? "is-open" : ""}`}
        onClick={() => setIsOpen((p) => !p)}
      >
        <span>{selected?.label}</span>
        <ChevronDown size={14} className="cldb-cselect-chevron" />
      </button>
      {isOpen && (
        <div className="cldb-cselect-menu">
          {options.map((opt) => (
            <button
              type="button"
              key={opt.value}
              className={`cldb-cselect-option ${opt.value === value ? "is-selected" : ""}`}
              onClick={() => {
                onChange(opt.value);
                setIsOpen(false);
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const ACTION_MENU_ITEMS = [
  { key: "edit", label: "Edit Client", icon: Pencil },
  { key: "note", label: "Add Note", icon: StickyNote },
  { key: "orders", label: "View Orders", icon: ClipboardList },
  { key: "payments", label: "View Payments", icon: CreditCard },
  { key: "activity", label: "View Activity Log", icon: Activity },
  { key: "toggle-status", label: "Suspend / Activate Client", icon: Power },
  { key: "delete", label: "Delete Client", icon: Trash2, danger: true },
];

function RowActionsMenu({ client, isOpen, onToggle, onAction }) {
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target))
        onToggle(null);
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [isOpen, onToggle]);

  return (
    <div className="cldb-row-actions-wrap" ref={wrapRef}>
      <button
        className="cldb-icon-btn"
        title="More"
        onClick={(e) => {
          e.stopPropagation();
          onToggle(isOpen ? null : client.id);
        }}
      >
        <MoreVertical size={15} />
      </button>
      {isOpen && (
        <div className="cldb-row-actions-menu">
          {ACTION_MENU_ITEMS.map((item) => {
            const ItemIcon = item.icon;
            return (
              <button
                key={item.key}
                className={`cldb-row-actions-item ${item.danger ? "is-danger" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggle(null);
                  onAction(item.key, client);
                }}
              >
                <ItemIcon size={14} />
                {item.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const STATIC_FIELD_TITLE = "Static filter — not connected to the API yet";

function StaticDot() {
  return <span className="cldb-sample-dot" title={STATIC_FIELD_TITLE} />;
}

function MoreFiltersModal({
  isOpen,
  onClose,
  appliedFilters,
  onApply,
  onReset,
  cityOptions,
}) {
  const [draft, setDraft] = useState(appliedFilters);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      setDraft(appliedFilters);
      setErrors({});
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const updateDraft = (key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: null }));
  };

  const validate = () => {
    const newErrors = {};

    const regFromOnly = draft.registrationDateFrom && !draft.registrationDateTo;
    const regToOnly = draft.registrationDateTo && !draft.registrationDateFrom;
    if (regFromOnly) newErrors.registrationDateTo = "Please select an end date too, or clear the start date";
    if (regToOnly) newErrors.registrationDateFrom = "Please select a start date too, or clear the end date";

    const lastFromOnly = draft.lastOrderFrom && !draft.lastOrderTo;
    const lastToOnly = draft.lastOrderTo && !draft.lastOrderFrom;
    if (lastFromOnly) newErrors.lastOrderTo = "Please select an end date too, or clear the start date";
    if (lastToOnly) newErrors.lastOrderFrom = "Please select a start date too, or clear the end date";
    
    const actFromOnly = draft.lastActivityFrom && !draft.lastActivityTo;

    const actToOnly = draft.lastActivityTo && !draft.lastActivityFrom;
    if (actFromOnly) newErrors.lastActivityTo = "Please select an end date too, or clear the start date";
    if (actToOnly) newErrors.lastActivityFrom = "Please select a start date too, or clear the end date";

    const rangeCheck = (minKey, maxKey, label) => {
      const min = draft[minKey];
      const max = draft[maxKey];
      if (min !== "" && max !== "" && Number(min) > Number(max)) {
        newErrors[maxKey] = `Max ${label} must be greater than or equal to min`;
      }
    };
    rangeCheck("minOrdersCount", "maxOrdersCount", "orders");
    rangeCheck("minTotalSpent", "maxTotalSpent", "amount");
    rangeCheck("lastActivityMinDays", "lastActivityMaxDays", "days");

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleApply = () => {
    if (validate()) onApply(draft);
  };

  return (
    <div className="cldb-modal-overlay" onClick={onClose}>
      <div className="cldb-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cldb-modal-header">
          <div>
            <h3>More Filters</h3>
            <p>Refine your search using advanced filters</p>
          </div>
          <button className="cldb-icon-btn" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <div className="cldb-modal-grid">
          <div className="cldb-field">
            <label>
              <span className="me-1">Registration Date Range</span>
            </label>
            <div className="cldb-range-inputs">
              <div className="cldb-range-input-group">
                <input
                  type="date"
                  className={errors.registrationDateFrom ? "is-invalid" : ""}
                  value={draft.registrationDateFrom}
                  onChange={(e) =>
                    updateDraft("registrationDateFrom", e.target.value)
                  }
                />
                {errors.registrationDateFrom && (
                  <span className="cldb-field-error">
                    {errors.registrationDateFrom}
                  </span>
                )}
              </div>
              <span>→</span>
              <div className="cldb-range-input-group">
                <input
                  type="date"
                  className={errors.registrationDateTo ? "is-invalid" : ""}
                  value={draft.registrationDateTo}
                  onChange={(e) =>
                    updateDraft("registrationDateTo", e.target.value)
                  }
                  min={draft.registrationDateFrom || undefined}
                />
                {errors.registrationDateTo && (
                  <span className="cldb-field-error">
                    {errors.registrationDateTo}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="cldb-field">
            <label>
              <span className="me-1">Last Order Date Range</span>
            </label>
            <div className="cldb-range-inputs">
              <div className="cldb-range-input-group">
                <input
                  type="date"
                  className={errors.lastOrderFrom ? "is-invalid" : ""}
                  value={draft.lastOrderFrom}
                  onChange={(e) => updateDraft("lastOrderFrom", e.target.value)}
                />
                {errors.lastOrderFrom && (
                  <span className="cldb-field-error">
                    {errors.lastOrderFrom}
                  </span>
                )}
              </div>
              <span>→</span>
              <div className="cldb-range-input-group">
                <input
                  type="date"
                  className={errors.lastOrderTo ? "is-invalid" : ""}
                  value={draft.lastOrderTo}
                  onChange={(e) => updateDraft("lastOrderTo", e.target.value)}
                  min={draft.lastOrderFrom || undefined}
                />
                {errors.lastOrderTo && (
                  <span className="cldb-field-error">{errors.lastOrderTo}</span>
                )}
              </div>
            </div>
          </div>

          <div className="cldb-field">
            <label>
              <span className="me-1">Lifetime Value Range (EGP)</span>
              <StaticDot />
            </label>
            <div className="cldb-range-inputs">
              <input type="number" placeholder="Min amount" />
              <span>-</span>
              <input type="number" placeholder="Max amount" />
            </div>
          </div>

          <div className="cldb-field">
            <label>
              <span className="me-1">Wallet Balance Range (EGP)</span>
              <StaticDot />
            </label>
            <div className="cldb-range-inputs">
              <input type="number" placeholder="Min balance" />
              <span>-</span>
              <input type="number" placeholder="Max balance" />
            </div>
          </div>

          <div className="cldb-field">
            <label>
              <span className="me-1">Last Activity Date Range</span>
            </label>
            <div className="cldb-range-inputs">
              <div className="cldb-range-input-group">
                <input
                  type="date"
                  className={errors.lastActivityFrom ? "is-invalid" : ""}
                  value={draft.lastActivityFrom}
                  onChange={(e) =>
                    updateDraft("lastActivityFrom", e.target.value)
                  }
                />
                {errors.lastActivityFrom && (
                  <span className="cldb-field-error">
                    {errors.lastActivityFrom}
                  </span>
                )}
              </div>
              <span>→</span>
              <div className="cldb-range-input-group">
                <input
                  type="date"
                  className={errors.lastActivityTo ? "is-invalid" : ""}
                  value={draft.lastActivityTo}
                  onChange={(e) =>
                    updateDraft("lastActivityTo", e.target.value)
                  }
                  min={draft.lastActivityFrom || undefined}
                />
                {errors.lastActivityTo && (
                  <span className="cldb-field-error">
                    {errors.lastActivityTo}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="cldb-field">
            <label>Risk Level</label>
            <select
              value={draft.riskFilter}
              onChange={(e) => updateDraft("riskFilter", e.target.value)}
            >
              {RISK_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="cldb-field">
            <label>Tier</label>
            <select
              value={draft.tierFilter}
              onChange={(e) => updateDraft("tierFilter", e.target.value)}
            >
              <option value="all">All</option>
              <option value="gold">Gold</option>
              <option value="silver">Silver</option>
              <option value="bronze">Bronze</option>
            </select>
          </div>

          <div className="cldb-field">
            <label>Status</label>
            <select
              value={draft.statusFilter}
              onChange={(e) => updateDraft("statusFilter", e.target.value)}
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Suspended</option>
            </select>
          </div>

          <div className="cldb-field">
            <label>City</label>
            <select
              value={draft.cityFilter}
              onChange={(e) => updateDraft("cityFilter", e.target.value)}
            >
              {cityOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="cldb-field">
            <label>
              <span className="me-1">Tags</span> <StaticDot />
            </label>
            <select defaultValue="">
              <option value="">Select tags</option>
            </select>
          </div>

          <div className="cldb-field">
            <label>
              <span className="me-1">Has Outstanding Balance</span>
              <StaticDot />
            </label>
            <select defaultValue="all">
              <option value="all">All</option>
            </select>
          </div>

          <div className="cldb-field">
            <label>
              <span className="me-1">Verified</span>
              <StaticDot />
            </label>
            <select defaultValue="all">
              <option value="all">All</option>
            </select>
          </div>

          <div className="cldb-field">
            <label>
              <span className="me-1">Has Complaints</span>
              <StaticDot />
            </label>
            <select defaultValue="all">
              <option value="all">All</option>
            </select>
          </div>

          <div className="cldb-field">
            <label>Has Orders</label>
            <select
              value={draft.hasOrderFilter}
              onChange={(e) => updateDraft("hasOrderFilter", e.target.value)}
            >
              {HAS_ORDER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="cldb-field">
            <label>
              <span className="me-1">Payment Method Used</span>
              <StaticDot />
            </label>
            <select defaultValue="all">
              <option value="all">All</option>
            </select>
          </div>

          <div className="cldb-field">
            <label>
              <span className="me-1">Number of Orders</span>
            </label>
            <div className="cldb-range-inputs">
              <div className="cldb-range-input-group">
                <input
                  type="number"
                  min="0"
                  placeholder="Min"
                  value={draft.minOrdersCount}
                  onChange={(e) =>
                    updateDraft("minOrdersCount", e.target.value)
                  }
                />
              </div>
              <span>-</span>
              <div className="cldb-range-input-group">
                <input
                  type="number"
                  min="0"
                  placeholder="Max"
                  className={errors.maxOrdersCount ? "is-invalid" : ""}
                  value={draft.maxOrdersCount}
                  onChange={(e) =>
                    updateDraft("maxOrdersCount", e.target.value)
                  }
                />
                {errors.maxOrdersCount && (
                  <span className="cldb-field-error">
                    {errors.maxOrdersCount}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="cldb-field">
            <label>
              <span className="me-1">Average Order Value (EGP)</span>
            </label>
            <div className="cldb-range-inputs">
              <div className="cldb-range-input-group">
                <input
                  type="number"
                  min="0"
                  placeholder="Min amount"
                  value={draft.minTotalSpent}
                  onChange={(e) => updateDraft("minTotalSpent", e.target.value)}
                />
              </div>
              <span>-</span>
              <div className="cldb-range-input-group">
                <input
                  type="number"
                  min="0"
                  placeholder="Max amount"
                  className={errors.maxTotalSpent ? "is-invalid" : ""}
                  value={draft.maxTotalSpent}
                  onChange={(e) => updateDraft("maxTotalSpent", e.target.value)}
                />
                {errors.maxTotalSpent && (
                  <span className="cldb-field-error">
                    {errors.maxTotalSpent}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="cldb-field">
            <label>
              <span className="me-1">Days Since Last Activity</span>
            </label>
            <div className="cldb-range-inputs">
              <div className="cldb-range-input-group">
                <input
                  type="number"
                  min="0"
                  placeholder="Min days"
                  value={draft.lastActivityMinDays}
                  onChange={(e) =>
                    updateDraft("lastActivityMinDays", e.target.value)
                  }
                />
              </div>
              <span>-</span>
              <div className="cldb-range-input-group">
                <input
                  type="number"
                  min="0"
                  placeholder="Max days"
                  className={errors.lastActivityMaxDays ? "is-invalid" : ""}
                  value={draft.lastActivityMaxDays}
                  onChange={(e) =>
                    updateDraft("lastActivityMaxDays", e.target.value)
                  }
                />
                {errors.lastActivityMaxDays && (
                  <span className="cldb-field-error">
                    {errors.lastActivityMaxDays}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="cldb-modal-footer">
          <button className="cldb-btn-outline" onClick={onReset}>
            Reset Filters
          </button>
          <div className="cldb-modal-footer-right">
            <button className="cldb-btn-outline" onClick={onClose}>
              Cancel
            </button>
            <button className="cldb-btn-primary" onClick={handleApply}>
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function GrowthBadge({ percentage, direction }) {
  const isUp = direction === "up";
  return (
    <span className={`cldb-growth cldb-growth--${isUp ? "up" : "down"}`}>
      {isUp ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
      {percentage}%
    </span>
  );
}

function Clients() {
  const { user } = useSelector((state) => state.auth);
  const token = user?.token;
  const navigate = useNavigate();

  const [clientsData, setClientsData] = useState([]);
  const [dashboard , setDashboard] = useState(null)
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState(null);

  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [tierFilter, setTierFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [riskFilter, setRiskFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("all");
  const [cityOptions, setCityOptions] = useState([{ value: "all", label: "City: All" }]);
  const [registrationDateFrom, setRegistrationDateFrom] = useState("");
  const [registrationDateTo, setRegistrationDateTo] = useState("");
  const [lastOrderFrom, setLastOrderFrom] = useState("");
  const [lastOrderTo, setLastOrderTo] = useState("");
  const [hasOrderFilter, setHasOrderFilter] = useState("all");
  const [minOrdersCount, setMinOrdersCount] = useState("");
  const [maxOrdersCount, setMaxOrdersCount] = useState("");
  const [minTotalSpent, setMinTotalSpent] = useState("");
  const [maxTotalSpent, setMaxTotalSpent] = useState("");
  const [lastActivityFrom, setLastActivityFrom] = useState("");
  const [lastActivityTo, setLastActivityTo] = useState("");
  const [lastActivityMinDays, setLastActivityMinDays] = useState("");
  const [lastActivityMaxDays, setLastActivityMaxDays] = useState("");
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const [openActionsRowId, setOpenActionsRowId] = useState(null);

  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: PER_PAGE,
    total: 0,
    last_page: 1,
  });

  const abortControllerRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchQuery(searchInput);
      setPagination((prev) => ({ ...prev, current_page: 1 }));
    }, 500);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const response = await axios.get(OPTIONS_API_URL, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const cities = response.data?.data?.cities || [];
        setCityOptions([
          { value: "all", label: "City: All" },
          ...cities.map((city) => ({ value: city, label: city })),
        ]);
      } catch (err) {
        console.error("Error fetching filter options:", err);
      }
    };
    if (token) fetchFilterOptions();
  }, [token]);

  const fetchClients = useCallback(
    async (page) => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setLoading(true);
      setErrors(null);

      try {
        const response = await axios.get(API_BASE_URL, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            page,
            per_page: PER_PAGE,
            search: searchQuery || undefined,
            city: cityFilter !== "all" ? cityFilter : undefined,
            created_before:
              registrationDateFrom && registrationDateTo
                ? registrationDateFrom
                : undefined,
            created_after:
              registrationDateFrom && registrationDateTo
                ? registrationDateTo
                : undefined,
            last_order_from:
              lastOrderFrom && lastOrderTo ? lastOrderFrom : undefined,
            last_order_to:
              lastOrderFrom && lastOrderTo ? lastOrderTo : undefined,
            has_order:
              hasOrderFilter === "yes"
                ? 1
                : hasOrderFilter === "no"
                  ? 0
                  : undefined,
            min_orders_count: minOrdersCount !== "" ? minOrdersCount : undefined,
            max_orders_count: maxOrdersCount !== "" ? maxOrdersCount : undefined,
            min_total_spent: minTotalSpent !== "" ? minTotalSpent : undefined,
            max_total_spent: maxTotalSpent !== "" ? maxTotalSpent : undefined,
            last_activity_from:
            lastActivityFrom && lastActivityTo ? lastActivityFrom : undefined,
            last_activity_to:
            lastActivityFrom && lastActivityTo ? lastActivityTo : undefined,
            last_activity_min_days:
            lastActivityMinDays !== "" ? lastActivityMinDays : undefined,
            last_activity_max_days:
            lastActivityMaxDays !== "" ? lastActivityMaxDays : undefined,
          },
          signal: controller.signal,
        });

        const { clients, pagination: paginationData } =
          response.data?.data || {};

        setDashboard(response.data?.data?.dashboard);
        console.log(response.data?.data?.dashboard);

        let result = clients || [];

        if (tierFilter !== "all") {
          result = result.filter(
            (client) =>
              getClientTier(client.points?.total_points) === tierFilter,
          );
        }

        if (statusFilter !== "all") {
          result = result.filter((client) =>
            statusFilter === "active" ? client.is_active : !client.is_active,
          );
        }

        if (riskFilter !== "all") {
          result = result.filter((client) => {
            const seed = typeof client.id === "number" ? client.id : 0;
            return getRiskLevel(seededValue(seed, 2, 95)) === riskFilter;
          });
        }

        setClientsData(result);
        if (paginationData) setPagination(paginationData);

        if (result.length === 0) {
          setErrors("لا يوجد عملاء مطابقين لهذا البحث أو الفلتر");
        }
      } catch (err) {
        if (axios.isCancel(err) || err.name === "CanceledError") return;
        console.error("Error fetching clients:", err);
        setErrors(
          err.response?.data?.message ||
            "حدث خطأ أثناء تحميل بيانات العملاء، حاول مرة أخرى",
        );
        setClientsData([]);
      } finally {
        setLoading(false);
      }
    },
    [
      token,
      searchQuery,
      tierFilter,
      statusFilter,
      riskFilter,
      cityFilter,
      registrationDateFrom,
      registrationDateTo,
      lastOrderFrom,
      lastOrderTo,
      hasOrderFilter,
      minOrdersCount,
      maxOrdersCount,
      minTotalSpent,
      maxTotalSpent,
      lastActivityFrom,
      lastActivityTo,
      lastActivityMinDays,
      lastActivityMaxDays,
    ],
  );

  useEffect(() => {
    fetchClients(pagination.current_page);
  }, [
    pagination.current_page,
    searchQuery,
    tierFilter,
    statusFilter,
    riskFilter,
    cityFilter,
    registrationDateFrom,
    registrationDateTo,
    lastOrderFrom,
    lastOrderTo,
    hasOrderFilter,
    token,
    minOrdersCount,
    maxOrdersCount,
    minTotalSpent,
    maxTotalSpent,
    lastActivityFrom,
    lastActivityTo,
    lastActivityMinDays,
    lastActivityMaxDays,
  ]);
  
  const goToPage = (page) => {
    if (page < 1 || page > pagination.last_page || page === pagination.current_page) return;
    setPagination((prev) => ({ ...prev, current_page: page }));
  };

  const renderPageNumbers = () => {
    const pages = [];
    const total = pagination.last_page;
    const current = pagination.current_page;
    const range = 1;

    for (let i = 1; i <= total; i++) {
      if (i === 1 || i === total || (i >= current - range && i <= current + range)) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== "...") {
        pages.push("...");
      }
    }

    return pages.map((p, idx) =>
      p === "..." ? (
        <span key={`dots-${idx}`} className="cldb-page-dots">
          …
        </span>
      ) : (
        <button
          key={p}
          className={`cldb-page-btn ${p === current ? "is-active" : ""}`}
          onClick={() => goToPage(p)}
        >
          {p}
        </button>
      ),
    );
  };

const isEmptyState = !loading && clientsData.length === 0;
const rows = clientsData;

const stats = useMemo(() => {
  const source = clientsData;
  const activeCount = source.filter((c) => c.is_active).length;
  const vipCount = source.filter(
    (c) => getClientTier(c.points?.total_points) === "gold",
  ).length;
  const newCount = source.filter((c) => daysAgo(c.created_at) <= 30).length;
  const atRiskCount = source.filter(
    (c) => !c.is_active || c.marked_for_deletion_at,
  ).length;
  return { activeCount, vipCount, newCount, atRiskCount };
}, [clientsData]);

  return (
    <div className="cldb-page">
      {/* ---------------- Stat cards ---------------- */}
      <div className="cldb-stats">
        <div className="cldb-stat-card">
          <div className=" d-flex gap-2">
            <div className="cldb-stat-icon cldb-stat-icon--indigo">
              <Users size={35} />
            </div>
            <div>
              <p className="cldb-stat-label">Total Clients</p>
              <p className="cldb-stat-value">
                {(pagination.total || rows.length).toLocaleString("en-US")}
              </p>
            </div>
          </div>
          <div className=" d-flex justify-content-end align-items-end h-100">
            {dashboard && (
              <GrowthBadge
                percentage={dashboard.total_client_growth_percentage}
                direction={dashboard.total_client_growth_direction}
              />
            )}
          </div>
        </div>

        <div className="cldb-stat-card">
          <div className=" d-flex gap-2">
            <div className="cldb-stat-icon cldb-stat-icon--green">
              <CheckCircle2 size={35} />
            </div>
            <div>
              <p className="cldb-stat-label">
                Active Clients{" "}
                <span
                  className="cldb-sample-dot"
                  title="Estimated from current page — connect an aggregate endpoint for a live count"
                />
              </p>
              <p className="cldb-stat-value">
                {stats.activeCount.toLocaleString("en-US")}
              </p>
            </div>
          </div>
        </div>

        <div className="cldb-stat-card">
          <div className=" d-flex gap-2">
            <div className="cldb-stat-icon cldb-stat-icon--gold">
              <Crown size={35} />
            </div>
            <div>
              <p className="cldb-stat-label">
                VIP Clients{" "}
                <span
                  className="cldb-sample-dot"
                  title="Estimated from current page — connect an aggregate endpoint for a live count"
                />
              </p>
              <p className="cldb-stat-value">
                {stats.vipCount.toLocaleString("en-US")}
              </p>
            </div>
          </div>
        </div>

        <div className="cldb-stat-card">
          <div className="d-flex gap-2">
            <div className="cldb-stat-icon cldb-stat-icon--blue">
              <UserPlus size={35} />
            </div>
            <div>
              <p className="cldb-stat-label">New This Month</p>
              <p className="cldb-stat-value">
                {(
                  dashboard?.new_client_total_key ?? stats.newCount
                ).toLocaleString("en-US")}
              </p>
            </div>
          </div>

          <div className="h-100 d-flex justify-content-end align-items-end">
            {dashboard && (
              <GrowthBadge
                percentage={dashboard.new_client_growth_percentage}
                direction={dashboard.new_client_growth_direction}
              />
            )}
          </div>
        </div>

        <div className="cldb-stat-card">
          <div className=" d-flex gap-2">
            <div className="cldb-stat-icon cldb-stat-icon--red">
              <AlertTriangle size={35} />
            </div>
            <div>
              <p className="cldb-stat-label">
                At Risk Clients{" "}
                <span
                  className="cldb-sample-dot"
                  title="Estimated from current page — connect an aggregate endpoint for a live count"
                />
              </p>
              <p className="cldb-stat-value">
                {stats.atRiskCount.toLocaleString("en-US")}
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* ---------------- Filter bar ---------------- */}
      <div className="cldb-toolbar">
        <div className="cldb-search">
          <Search size={16} className="cldb-search-icon" />
          <input
            type="text"
            placeholder="Search by name, phone, email or ID..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>

        <CustomSelect
          value={statusFilter}
          onChange={(v) => {
            setStatusFilter(v);
            setPagination((prev) => ({ ...prev, current_page: 1 }));
          }}
          options={[
            { value: "all", label: "Status: All" },
            { value: "active", label: "Active" },
            { value: "inactive", label: "Suspended" },
          ]}
        />

        <CustomSelect
          value={tierFilter}
          onChange={(v) => {
            setTierFilter(v);
            setPagination((prev) => ({ ...prev, current_page: 1 }));
          }}
          options={[
            { value: "all", label: "Tier: All" },
            { value: "gold", label: "Gold" },
            { value: "silver", label: "Silver" },
            { value: "bronze", label: "Bronze" },
          ]}
        />

        <CustomSelect
          value={riskFilter}
          onChange={(v) => {
            setRiskFilter(v);
            setPagination((prev) => ({ ...prev, current_page: 1 }));
          }}
          options={RISK_OPTIONS}
        />

        <CustomSelect
          value={cityFilter}
          onChange={(v) => {
            setCityFilter(v);
            setPagination((prev) => ({ ...prev, current_page: 1 }));
          }}
          options={cityOptions}
        />

        <button
          className="cldb-btn-outline"
          onClick={() => setMoreFiltersOpen(true)}
        >
          <SlidersHorizontal size={16} />
          More Filters
        </button>

        <button className="cldb-btn-outline">
          <Download size={16} />
          Export
        </button>

        <button className="cldb-btn-primary">
          <Plus size={16} />
          Add Client
        </button>
      </div>
      {/* ---------------- Table ---------------- */}
      <div className="cldb-table-card">
        {loading ? (
          <div className="cldb-loading">
            <span className="cldb-spinner" aria-label="Loading clients" />
          </div>
        ) : (
          <>
            {!isEmptyState && (
              <div className="cldb-table-scroll">
                <table className="cldb-table">
                  <thead>
                    <tr>
                      <th className="cldb-th-check">
                        <input type="checkbox" aria-label="Select all" />
                      </th>
                      <th>Client</th>
                      <th>Tier</th>
                      <th>Status</th>
                      <th>Risk</th>
                      <th>Lifetime Value</th>
                      <th>Wallet</th>
                      <th>Last Order</th>
                      <th>Last Activity</th>
                      <th className="cldb-th-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((client, idx) => {
                      const tier = getClientTier(client.points?.total_points);
                      const riskScore = seededValue(
                        typeof client.id === "number" ? client.id : idx + 1,
                        2,
                        95,
                      );
                      const risk = getRiskLevel(riskScore);
                      const wallet = seededValue(
                        (typeof client.id === "number" ? client.id : idx + 1) *
                          7,
                        0,
                        4200,
                      );
                      const lastOrderLabel = client.last_ordered_at
                        ? formatDate(client.last_ordered_at)
                        : "No orders yet";
                      const tags = [];
                      if (tier === "gold") tags.push("VIP");
                      if (!client.is_active || client.marked_for_deletion_at)
                        tags.push("At Risk");
                      if (daysAgo(client.created_at) <= 14) tags.push("New");
                      if ((client.orders_count || 0) >= 10)
                        tags.push("Frequent");

                      return (
                        <tr
                          key={client.id}
                          className={client.isSample ? "cldb-row--sample" : ""}
                        >
                          <td>
                            <input
                              type="checkbox"
                              aria-label={`Select ${client.name}`}
                            />
                          </td>
                          <td>
                            <div className="cldb-client-cell">
                              <img
                                className="cldb-avatar"
                                src={
                                  client.profile_photo_path
                                    ? `${IMAGE_BASE_URL}${client.profile_photo_path}`
                                    : profileStaff
                                }
                                alt={client.name}
                              />
                              <div>
                                <p className="cldb-client-name">
                                  {client.name || "لا يوجد اسم"}
                                  {client.isSample && (
                                    <span
                                      className="cldb-sample-dot"
                                      title="Sample row — added to fill out the layout"
                                    />
                                  )}
                                </p>
                                <div className="cldb-client-meta text-start">
                                  <p className="m-0">{client.reference}</p>
                                  <p className="m-0">
                                    {client.phone_number || "لا يوجد بيانات"}
                                  </p>
                                </div>
                                {tags.length > 0 && (
                                  <div className="cldb-tags d-flex align-items-center">
                                    {tags.map((t) => (
                                      <span
                                        key={t}
                                        className={`cldb-tag cldb-tag--${t.toLowerCase().replace(" ", "-")}`}
                                      >
                                        {t}
                                      </span>
                                    ))}
                                    <span
                                      className="cldb-sample-dot"
                                      title="Sample row — added to fill out the layout"
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td>
                            {(() => {
                              const TierIcon = TIER_ICON[tier];
                              return (
                                <span
                                  className={`cldb-tier cldb-tier--${tier}`}
                                >
                                  <TierIcon
                                    size={13}
                                    className="cldb-tier-icon"
                                  />
                                  {TIER_LABEL[tier]}
                                </span>
                              );
                            })()}
                          </td>
                          <td>
                            <span
                              className={`cldb-status cldb-status--${client.is_active ? "active" : "suspended"}`}
                            >
                              <span className="cldb-status-dot" />
                              {client.is_active ? "Active" : "Suspended"}
                            </span>
                          </td>
                          <td>
                            <div className="text-start ps-3">
                              <span
                                className={`cldb-risk cldb-risk--${risk}`}
                                title="Estimated — not yet provided by the API"
                              >
                                <span
                                  className="cldb-risk-gauge"
                                  style={{ "--risk-pct": `${riskScore}%` }}
                                >
                                  <span className="cldb-risk-gauge-track">
                                    {riskScore}
                                  </span>
                                </span>
                                {RISK_LABEL[risk]}
                                <span className="cldb-sample-dot" />
                              </span>
                            </div>
                          </td>
                          <td className="cldb-money">
                            {formatMoney(client.orders_total_sum)}
                          </td>
                          <td
                            className="cldb-money"
                            title="Estimated — not yet provided by the API"
                          >
                            <span className="me-1">
                              {wallet.toLocaleString("en-US")} EGP
                            </span>
                            <span className="cldb-sample-dot my-0" />
                          </td>
                          <td>
                            <p className="cldb-order-main">{lastOrderLabel}</p>
                            {client.orders_count ? (
                              <p className="cldb-order-sub">
                                {client.orders_count} orders total
                              </p>
                            ) : null}
                          </td>
                          <td>
                            <div className="cldb-activity">
                              <span>
                                {client.user_last_activity?.last_active_human ||
                                  "لا يوجد بيانات"}
                              </span>
                            </div>
                          </td>
                          <td>
                            <div className="cldb-actions">
                              <button
                                className="cldb-icon-btn"
                                title="View client"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!client.isSample)
                                    navigate(`/clients/${client.id}`);
                                }}
                              >
                                <Eye size={15} />
                              </button>
                              <button
                                className="cldb-icon-btn"
                                title="Call client"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Phone size={15} />
                              </button>
                              <button
                                className="cldb-icon-btn"
                                title="Message client"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <MessageCircle size={15} />
                              </button>
                              {!client.isSample && (
                                <RowActionsMenu
                                  client={client}
                                  isOpen={openActionsRowId === client.id}
                                  onToggle={setOpenActionsRowId}
                                  onAction={(actionKey, c) => {
                                    console.log(actionKey, c.id);
                                  }}
                                />
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {isEmptyState && (
              <div className="mt-4">
                <NotFound
                  paragraphText={
                    "No Clients yet. All your clients will appear here once you get started."
                  }
                />
              </div>
            )}

            {!isEmptyState && (
              <div className="cldb-pagination">
                <p className="cldb-pagination-count">
                  Showing{" "}
                  {rows.length
                    ? (pagination.current_page - 1) * pagination.per_page + 1
                    : 0}{" "}
                  to{" "}
                  {(pagination.current_page - 1) * pagination.per_page +
                    rows.length}{" "}
                  of {(pagination.total || rows.length).toLocaleString("en-US")}{" "}
                  results
                </p>

                {pagination.last_page > 1 && (
                  <div className="cldb-pagination-controls">
                    <button
                      className="cldb-page-btn"
                      disabled={pagination.current_page === 1}
                      onClick={() => goToPage(pagination.current_page - 1)}
                    >
                      <ChevronLeft size={15} />
                    </button>
                    {renderPageNumbers()}
                    <button
                      className="cldb-page-btn"
                      disabled={
                        pagination.current_page === pagination.last_page
                      }
                      onClick={() => goToPage(pagination.current_page + 1)}
                    >
                      <ChevronRight size={15} />
                    </button>
                  </div>
                )}

                <select
                  className="cldb-select cldb-select--sm"
                  defaultValue={PER_PAGE}
                >
                  <option value={PER_PAGE}>{PER_PAGE} / page</option>
                </select>
              </div>
            )}
          </>
        )}
      </div>
      {/* Filter Modal */}
      <MoreFiltersModal
        isOpen={moreFiltersOpen}
        onClose={() => setMoreFiltersOpen(false)}
        cityOptions={cityOptions}
        appliedFilters={{
          tierFilter,
          statusFilter,
          riskFilter,
          cityFilter,
          registrationDateFrom,
          registrationDateTo,
          lastOrderFrom,
          lastOrderTo,
          hasOrderFilter,
          minOrdersCount,
          maxOrdersCount,
          minTotalSpent,
          maxTotalSpent,
          lastActivityFrom,
          lastActivityTo,
          lastActivityMinDays,
          lastActivityMaxDays,
        }}
        onApply={(draft) => {
          setTierFilter(draft.tierFilter);
          setStatusFilter(draft.statusFilter);
          setRiskFilter(draft.riskFilter);
          setCityFilter(draft.cityFilter);
          setRegistrationDateFrom(draft.registrationDateFrom);
          setRegistrationDateTo(draft.registrationDateTo);
          setLastOrderFrom(draft.lastOrderFrom);
          setLastOrderTo(draft.lastOrderTo);
          setHasOrderFilter(draft.hasOrderFilter);
          setMinOrdersCount(draft.minOrdersCount);
          setMaxOrdersCount(draft.maxOrdersCount);
          setMinTotalSpent(draft.minTotalSpent);
          setMaxTotalSpent(draft.maxTotalSpent);
          setLastActivityFrom(draft.lastActivityFrom);
          setLastActivityTo(draft.lastActivityTo);
          setLastActivityMinDays(draft.lastActivityMinDays);
          setLastActivityMaxDays(draft.lastActivityMaxDays);
          setPagination((prev) => ({ ...prev, current_page: 1 }));
          setMoreFiltersOpen(false);
        }}
        onReset={() => {
          setTierFilter("all");
          setStatusFilter("all");
          setRiskFilter("all");
          setCityFilter("all");
          setRegistrationDateFrom("");
          setRegistrationDateTo("");
          setLastOrderFrom("");
          setLastOrderTo("");
          setHasOrderFilter("all");
          setMinOrdersCount("");
          setMaxOrdersCount("");
          setMinTotalSpent("");
          setMaxTotalSpent("");
          setLastActivityFrom("");
          setLastActivityTo("");
          setLastActivityMinDays("");
          setLastActivityMaxDays("");
          setPagination((prev) => ({ ...prev, current_page: 1 }));
          setMoreFiltersOpen(false);
        }}
      />
    </div>
  );
}

export default Clients;
